import Vue from 'vue';
import {Prop, Watch} from 'vue-property-decorator';
import Component, {mixins} from 'vue-class-component';
import {mapGetters} from 'vuex';
import {
  CLEAR_IS_GUEST_ORDER,
  CLEAR_BUYER_DATA,
  CLEAR_SESSION,
  CLEAR_USER,
  CLEAR_REGISTRATION_CODES,
  UPDATE_CLIENTS,
  UPDATE_EVENTS,
  UPDATE_PRODUCTS,
  UPDATE_SLOTS,
} from '../../../store/commits';
import CustomerForm from '../../Checkout/CustomerForm/CustomerForm.vue';
import VisitorSurveyComponent from '../../Checkout/VisitorSurveyComponent/VisitorSurveyComponent.vue';
import PaymentType from '../../Checkout/PaymentType/PaymentType.vue';
import CustomerData from '../../Checkout/CustomerData/CustomerData.vue';
import CartView from '../../Cart/CartView/CartView.vue';
import {Client, Country, Event, PrivacyStatement, Product, Slot, TermsAndConditions, VisitorSegment} from '../../../Models';
import Vvs from '../../Icons/Vvs.vue';
import {CustomerDataDTO, PublicTransportConfigDTO, SessionDTO, VisitorSurveyResultDTO} from '../../../DTOs';
import {AuthService, PrivacyStatementService, TermsAndConditionsService} from '../../../Services';
import {VuexObjectMixin} from '../../../Mixins';
import {AddressTypeEnum, VisitorSegmentEnum} from '../../../Enums';
import PersonalUseModal from '../../Components/PersonalUseModal/PersonalUseModal.vue';
import RegistrationPublicTransportModal from '../../Components/RegistrationPublicTransportModal/RegistrationPublicTransportModal.vue';
import Modal from '../../Modal/Modal.vue';
import Check from '../../Icons/Check.vue';
import {ApiErrorHandler} from '../../../Errors/ApiErrorHandler';
import {AdditionalNoticeService} from '../../../Services/AdditionalNoticeService';
import {AdditionalNotice} from '../../../Models/AdditionalNotice';
import { TicketDownloadResponse } from '../../Checkout/CheckoutSuccessView/CheckoutSuccessView';
import {DEFAULT_LANGUAGE} from '../../../constants';

@Component({
  computed: {
    ...mapGetters({
      userLanguage: 'getUserLanguage',
      initialBuyerData: 'getBuyerData',
      sessionDTO: 'getSession',
      registrationCodes: 'getCurrentRegistrationCodes',
      sessionSTO: 'getSession',
      countries: 'getCountries',
      states: 'getStates',
      visitorSegments: 'getVisitorSegments',
      exhibitorNames: 'getExhibitorNames',
      exhibitorAdvertisings: 'getExhibitorAdvertisings',
      isGuestOrder: 'getIsGuestOrder',
    }),
  },
  components: {
    Vvs,
    CustomerForm,
    CustomerData,
    Check,
    CartView,
    VisitorSurveyComponent,
    PaymentType,
    PersonalUseModal,
    RegistrationPublicTransportModal,
    Modal,
  },
})
export default class RegisterTicketView extends mixins(VuexObjectMixin) {
  @Prop({type: String, required: true}) inventoryUrl: string | undefined;
  @Prop({type: String, required: true}) loginUrl: string | undefined;
  @Prop({type: String, required: true}) ticketShopUrl: string | undefined;
  @Prop({type: String, required: true}) loginPageUrl: string | undefined;

  // instance data
  private exhibitorNames!: Array<string>;
  private exhibitorAdvertisings!: Array<any>;
  private visitorSegments!: Array<VisitorSegment>;
  private sessionDTO: SessionDTO | null | undefined;
  private countries!: Array<Country>;
  private ticketClaimSuccess: boolean = false;
  private isGuestOrder!: boolean | null;

  private personalizationValid: boolean = true;
  private showErrors: boolean = false;

  // dtos
  private personalizationDTO = new CustomerDataDTO();

  private privacyStatementService!: PrivacyStatementService;
  private termsAndConditionsService!: TermsAndConditionsService;
  private additionalNoticeService!: AdditionalNoticeService;
  private authService!: AuthService;

  private initialBuyerData!: CustomerDataDTO | null;
  private registrationCodes!: Array<string> | null;

  private currentClient: string | null = null;
  private privacyStatements: any = {};
  private agb: any = {};
  private additionalNotices: Array<AdditionalNotice> = [];
  private selectedSlots: { [key: number]: string | number | null } = {};
  private isZipValid: boolean = true;
  private hideSurvey: boolean = false;

  private ticketCodes: Array<string> = [];
  private downloadLinkProgress: number = 0;
  private isRequestingDownloadLinks: boolean = false;
  private ticketDownloadResponse: TicketDownloadResponse | null = null;

  created() {
    this.privacyStatementService = new PrivacyStatementService(this.inventoryUrl);
    this.termsAndConditionsService = new TermsAndConditionsService(this.inventoryUrl);
    this.additionalNoticeService = new AdditionalNoticeService(this.inventoryUrl);
    this.authService = new AuthService(this.loginUrl);
    // first time loading the checkout page, no edit was made on the checkout data
    if (this.initialBuyerData && !(this.personalizationDTO.email.length > 0)) {
      this.initialBuyerData.publicTransportConfigs = [];
      this.personalizationDTO = (new CustomerDataDTO()).fromData(this.initialBuyerData);
    }
  }

  async mounted() {
    this.isRequesting = true;

    // no cart must be present when registering a ticket
    const product = this.activeProduct;
    if (!product || !this.hasRegistrationCodes) {
      this.isRequesting = false;
      this.showToast(<string>this.$t('toasts.codeOrProductNotFound'));
      this.$store.commit(CLEAR_REGISTRATION_CODES);
      //setTimeout(() => this.redirectToTicketShop(), 3000)
      return;
    }

    if (!this.initialBuyerData && this.loginPageUrl && product.registrationRequired === 'Ja') {
      // not logged in/as guest: redirect to login page
      this.redirectToLogin();
    } else {
      try {
        await this.fetchData();
        await this.fetchUserConsent();
      } finally {
        this.isRequesting = false;
      }
    }
  }

  async fetchData() {
    try {
      await this.$store.dispatch('fetchMiscData', this.inventoryUrl);
      this.products.forEach(p => Vue.set(this.selectedSlots, p.id, null));
    } catch (e) {
      this.handleApiError(e);
    }
  }

  async fetchUserConsent() {
    // fetch DPC & TAC of different clients which the user has to agree to, also fetch ANs
    const clientIds = this.clients.map(c => c.id);
    const statements = await this.privacyStatementService.listCached<PrivacyStatement>('', {clientId: `[${clientIds.join(',')}]`});
    const termsAndConditions = await this.termsAndConditionsService.listCached<TermsAndConditions>(
      '',
      {clientId: `[${clientIds.join(',')}]`},
    );
    const additionalNotices = await this.additionalNoticeService.listCached<AdditionalNotice>('', {clientId: `[${clientIds.join(',')}]`});

    this.clients.forEach(client => {
      const ps = statements.find(p => p.clientId === client.id);
      const tac = termsAndConditions.find(p => p.clientId === client.id);
      const an = additionalNotices.find(p => p.clientId === client.id);
      if (ps) {
        Vue.set(this.privacyStatements, `${client.id}`, ps);
      }
      if (tac) {
        Vue.set(this.agb, `${client.id}`, tac);
      }
      if (an) {
        Vue.set(this.additionalNotices, `${client.id}`, an);
      }
    });

    // unset null-values
    // this.additionalNotices = Object.fromEntries(Object.entries(this.additionalNotices).filter(([_, v]) => v != null));
    this.privacyStatements = Object.fromEntries(Object.entries(this.privacyStatements).filter(([_, v]) => v != null));
    this.agb = Object.fromEntries(Object.entries(this.agb).filter(([_, v]) => v != null));
  }

  getEventById(eventId: number | string): Event | undefined {
    return this.events.find(e => e.id === eventId);
  }

  getSlotsByIds(slotIds: Array<number>): Array<Slot> {
    return this.slots.filter(s => slotIds.find(sId => sId === s.id) !== undefined);
  }

  getProductById(productId): Product | null {
    return this.products.find(p => p.id === Number.parseInt(`${productId}`, 10)) || null;
  }

  getSlotById(slotId): Slot | null {
    return this.slots.find(s => s.id === Number.parseInt(`${slotId}`, 10)) || null;
  }

  selectSlot(product: Product, slot: Slot) {
    Vue.set(this.selectedSlots, product.id, slot.id);
  }

  updatePersonalizationDTO(updateData: { dto: CustomerDataDTO, missingRequiredFields: Array<string> }) {
    Object.keys(updateData.dto).forEach((k) => {
      Vue.set(this.personalizationDTO, k, updateData.dto[k]);
    });
    this.personalizationValid = updateData.missingRequiredFields.length === 0 && this.validateSurveys();
  }

  cancelRegistration() {
    if (this.ticketShopUrl) {
      this.$store.commit(CLEAR_REGISTRATION_CODES);
      this.$store.commit(CLEAR_BUYER_DATA);
      this.$store.commit(UPDATE_EVENTS, []);
      this.$store.commit(UPDATE_SLOTS, []);
      this.$store.commit(UPDATE_PRODUCTS, []);
      this.$store.commit(UPDATE_CLIENTS, []);

      window.location.href = this.ticketShopUrl;
    }
  }

  async processRegistration() {
    this.showErrors = true;

    if (!this.hasRegistrationCodes || !this.checkoutFormIsValid) {
      return;
    }

    if (this.registrationCodes && this.registrationCodes.length > 0) {
      try {
        this.isRequesting = true;
        const slots: Array<{ productId: number, slotId: number | null }> = [];
        Object.entries(this.selectedSlots).forEach(([productId, slotId]) => slots.push({
          productId: Number.parseInt(productId, 10),
          slotId: slotId ? Number.parseInt(`${slotId}`, 10) : null,
        }));
        const redeemResponse = await this.orderService.redeemTicketCode(this.personalizationDTO, this.registrationCodes, slots, this.userLanguage);
        this.ticketCodes = redeemResponse.ticketCodes;
        if (this.shouldGenerateDownloadLinks) {
          await this.provideDownloadLinks(redeemResponse.jwt);
        } else {
          this.ticketClaimSuccess = true;
          this.isRequesting = false;
          await this.clearCartAndUserData();
        }
      } catch (e) {
        this.handleApiError(e);
      }
    }
  }

  async clearCartAndUserData() {
    this.$store.commit(CLEAR_REGISTRATION_CODES);
    this.$store.commit(CLEAR_BUYER_DATA);
    this.$store.commit(CLEAR_IS_GUEST_ORDER); // See #LMSTICKET-877
    this.$store.commit(UPDATE_EVENTS, []);
    this.$store.commit(UPDATE_SLOTS, []);
    this.$store.commit(UPDATE_PRODUCTS, []);
    this.$store.commit(UPDATE_CLIENTS, []);
    await this.logout(); // See #LMSTICKET-877
  }

  async getDownloadLinks(authJWT: string): Promise<TicketDownloadResponse | null> {
    try {
      return await this.orderService.getDownloadLinksRegistration(this.ticketCodes[0], this.userLanguage, authJWT);
    } catch (e) {
      this.handleApiError(e);
      return null;
    }
  }

  async getDownloadLinksProgress(authJWT: string) {
    try {
      return await this.orderService.getDownloadLinksProgressRegistration(this.ticketCodes[0], this.userLanguage, authJWT);
    } catch (e) {
      this.handleApiError(e);
      return false;
    }
  }

  async provideDownloadLinks(authJWT: string) {
    let downloadLinksProgressResult = {
      status: 0,
      progress: 0,
    };
    this.isRequestingDownloadLinks = true;
    // downloadLinksProgressResult = await this.getDownloadLinksProgress(authJWT);
    this.downloadLinkProgress = downloadLinksProgressResult.progress;
    const getDownloadLinksIntervall = setInterval(
      async () => {
        if (downloadLinksProgressResult.status === 200) {
          clearInterval(getDownloadLinksIntervall);
          if (this.downloadLinkProgress < 80) {
            this.downloadLinkProgress = 80;
          }
          const downloadLinksResult = await this.getDownloadLinks(authJWT);
          if (downloadLinksResult) {
            this.downloadLinkProgress = 100;
            this.ticketDownloadResponse = downloadLinksResult;
            this.ticketClaimSuccess = true;
            this.isRequesting = false;
            await this.clearCartAndUserData();
          }
        } else {
          downloadLinksProgressResult = await this.getDownloadLinksProgress(authJWT);
          if (downloadLinksProgressResult !== null) {
            this.downloadLinkProgress = downloadLinksProgressResult.progress;
          } else {
            clearInterval(getDownloadLinksIntervall);
          }
        }
      },
      5000,
    );
  }

  handleApiError(e) {
    console.error(e);
    const errorHandler = new ApiErrorHandler();
    errorHandler.handle(e);
  }

  /**
   * Call the validate function on each of the visitor survey components.
   * As we are defining a vue ref in a for loop, this.$refs[<surveyRefName>] is an array with only a single item.
   */
  validateSurveys() {
    let valid = true;
    this.events.forEach(e => {
      const componentRefName = `survey${e.id}`;
      // @ts-ignore
      if (this.$refs[componentRefName] && this.$refs[componentRefName].length > 0) {
        // @ts-ignore
        valid = valid && this.$refs[componentRefName][0].validate();
      }
    });
    return valid;
  }

  hasSurvey(event: Event): boolean {
    return event.visitorSurvey !== null;
  }

  getInitialSurveyData(event: Event) {
    const key = this.getEventResultKey(event.id);
    if (this.personalizationDTO.surveys && this.personalizationDTO.surveys[key]) {
      return this.personalizationDTO.surveys[key];
    }
    return null;
  }

  updateSurveyData(data: VisitorSurveyResultDTO) {
    this.personalizationDTO.surveys[this.getEventResultKey(data.eventId)] = data;
  }

  getEventResultKey(id: number | string) {
    return `event_${id}`;
  }

  openPrivacyModal(client: Client) {
    this.currentClient = `${client.id}`;
    // @ts-ignore
    this.$refs.privacyModal.showModal();
  }

  openAGBModal(client: Client) {
    this.currentClient = `${client.id}`;
    // @ts-ignore
    this.$refs.agbModal.showModal();
  }

  setIsZipValid(value: boolean) {
    this.isZipValid = value;
  }

  @Watch('initialBuyerData')
  onBuyerDataUpdate(newVal: CustomerDataDTO) {
    if (newVal && !(this.personalizationDTO.email.length > 0)) {
      this.personalizationDTO = (new CustomerDataDTO()).fromData(newVal);
    }
    if (!newVal && !this.ticketClaimSuccess && !this.isRequestingDownloadLinks) {
      this.personalizationDTO = new CustomerDataDTO();
      this.isRequesting = true;
      this.redirectToLogin();
    }
  }

  @Watch('personalizationDTO.usePublicTransport')
  onPublicTransportChange(newVal: boolean, oldVal: boolean) {
    if (!newVal) {
      this.submitPublicTransport([]);
    }
  }

  redirectToLogin() {
    window.location.href = `${this.loginPageUrl}?returnUrl=${encodeURIComponent(window.location.href)}`;
  }

  redirectToTicketShop() {
    window.location.href = `${this.ticketShopUrl}`;
  }

  submitPublicTransport(pubTransConfigs: Array<PublicTransportConfigDTO>) {
    this.personalizationDTO.publicTransportConfigs = pubTransConfigs;
    this.personalizationDTO.usePublicTransport = this.personalizationDTO.publicTransportConfigs.filter(c => c.usePubTrans).length !== 0;
  }

  get hasExhibitorAdvertising() {
    return this.exhibitorAdvertisings.length > 0 && this.exhibitorNames.length > 0;
  }

  get disabledPersonalizationFields() {
    if (this.initialBuyerData) {
      return ['salutation', 'title', 'firstName', 'lastName', 'email', 'addressType', 'company', 'additionalInformation', 'street', 'houseNr', 'zip', 'city', 'country', 'state'];
    }
    return [];
  }

  get requiredPersonalizationFields() {
    const fields = ['salutation', 'firstName', 'lastName', 'email', 'addressType', 'street', 'houseNr', 'zip', 'city', 'country'];
    if (this.isBusinessAddress) {
      fields.push('company');
    }
    return fields;
  }

  get isBusinessAddress() {
    return this.personalizationDTO.addressType === AddressTypeEnum.COMMERCIAL_BUSINESS.value;
  }

  get isPrivateAddress() {
    return this.personalizationDTO.addressType === AddressTypeEnum.PRIVATE.value;
  }

  get loginPageUrlWithReturnUrl() {
    return `${this.loginPageUrl}?returnUrl=${encodeURIComponent(window.location.href)}&options[]=registration&options[]=login`;
  }

  get isAuthenticated() {
    return this.sessionDTO !== undefined && this.sessionDTO !== null && Boolean(this.sessionDTO.session);
  }

  get activeProduct() {
    if (this.products && this.products.length > 0) {
      return this.products[0];
    }
    return null;
  }

  get hasExhibitorLogo() {
    return this.exhibitorAdvertisings.length > 0 && this.exhibitorAdvertisings[0].field === 'logo';
  }

  get exhibitorLogoUrl() {
    if (this.hasExhibitorLogo) {
      return this.exhibitorAdvertisings[0].data.config.content;
    }
    return '';
  }

  additionalNoticeHtmlFor(clientId: number | string): string {
    const notice = this.additionalNotices.find(x => x !== undefined && x.clientId === parseInt(clientId.toString(), 10));
    if (notice) {
      if (this.userLanguage !== DEFAULT_LANGUAGE) {
        const translations = notice.translations;
        if (translations && this.userLanguage in translations) {
          return translations[this.userLanguage].html || '';
        }
      }
      return notice?.html || '';
    }
    return '';
  }

  get fallbackAdditionalNoticeHtml(): string {
    const notice = this.additionalNotices.find(x => x !== undefined);
    if (notice) {
      if (this.userLanguage !== DEFAULT_LANGUAGE) {
        const translations = notice.translations;
        if (translations && this.userLanguage in translations) {
          return translations[this.userLanguage].html || '';
        }
      }
      return notice?.html || '';
    }
    return '';
  }
  get activeVisitorSegments() {
    const segmentIds = this.products.map(p => p.visitorSegmentId);
    return this.visitorSegments.filter(vs => segmentIds.includes(vs.id as number));
  }

  get isAbbau() {
    return this.activeVisitorSegments.find(vs => vs.title === VisitorSegmentEnum.ABBAU) !== undefined;
  }

  get isAufbau() {
    return this.activeVisitorSegments.find(vs => vs.title === VisitorSegmentEnum.AUFBAU) !== undefined;
  }

  get isAussteller() {
    return this.activeVisitorSegments.find(vs => vs.title === VisitorSegmentEnum.AUSSTELLER) !== undefined;
  }

  get isBesucher() {
    return this.activeVisitorSegments.find(vs => vs.title === VisitorSegmentEnum.BESUCHER) !== undefined;
  }

  get isPresse() {
    return this.activeVisitorSegments.find(vs => vs.title === VisitorSegmentEnum.PRESSE) !== undefined;
  }

  get isVIP() {
    return this.activeVisitorSegments.find(vs => vs.title === VisitorSegmentEnum.VIP) !== undefined;
  }

  get hasSurveys(): boolean {
    const surveys = this.events.map(e => e.visitorSurvey);
    return surveys.filter(s => s !== null).length > 0;
  }

  get addressTypeChoices() {
    return [
      {value: AddressTypeEnum.NONE.value, text: AddressTypeEnum.NONE.title[this.userLanguage]},
      {value: AddressTypeEnum.PRIVATE.value, text: AddressTypeEnum.PRIVATE.title[this.userLanguage]},
      {value: AddressTypeEnum.COMMERCIAL_BUSINESS.value, text: AddressTypeEnum.COMMERCIAL_BUSINESS.title[this.userLanguage]},
    ];
  }

  get hasSelectedSlots() {
    return Object.values(this.selectedSlots).filter(s => s).length > 0;
  }

  get checkoutUrl() {
    return window.location.href.split('?')[0];
  }

  get hasRegistrationCodes() {
    return this.registrationCodes !== null && this.registrationCodes.length > 0;
  }

  get enablePersonalization(): boolean {
    if (this.activeProduct) {
      return ['Ja', 'Optional'].indexOf(this.activeProduct.registrationRequired) >= 0;
    }
    return false;
  }

  get hasRequiredData() {
    // slots are not mandatory...
    return this.countries.length > 0 && this.events.length > 0 && this.products.length > 0 && this.clients.length > 0;
  }

  get selectedPrivacyStatement() {
    if (this.currentClient) {
      return this.privacyStatements[this.currentClient] || null;
    }
    return null;
  }

  get selectedAGB() {
    if (this.currentClient) {
      return this.agb[this.currentClient] || null;
    }
    return null;
  }

  get anyProductHasPubTransIncluded() {
    return this.products.filter(p => p.localPubTransIncluded === 1).length > 0;
  }

  get hasSelectedPubTrans(): boolean {
    return this.personalizationDTO.usePublicTransport;
  }

  get isPublicTransportValid() {
    if (!this.hasSelectedPubTrans) {
      return true;
    }

    const pubTransConfigs = this.personalizationDTO.publicTransportConfigs.filter(c => c.usePubTrans);

    if (pubTransConfigs.length) {
      const pubTransConfig = pubTransConfigs[0];
      if (pubTransConfig.usePubTrans) {
        if (this.productIsMultipleDays) {
          return (pubTransConfig.passengerName !== '' && pubTransConfig.passengerSurname !== '');
        }
        return (pubTransConfig.passengerName !== '' && pubTransConfig.passengerSurname !== '' && pubTransConfig.selectedDate !== null);
      }
    }

    return true;
  }

  get productIsMultipleDays(): boolean {
    return this.productAdmissionDays > 1 || this.isAussteller;
  }

  get productAdmissionDays(): number {
    if (this.activeProduct) {
      return this.activeProduct.numberFirstDayAdmissions;
    }
    return 1;
  }

  hasNameAndSurname(name: string): boolean {
    return name.trim().split(' ').length > 1;
  }

  get checkoutFormIsValid() {
    const checkedFallbacks = this.personalizationDTO.fallbackPrivacyChecked && this.personalizationDTO.fallbackAgbChecked;
    const checkedClientConsents = this.clientsWithConsentExist && this.acceptedClientsAGB && this.acceptedClientsPrivacyStatements;
    const productsWithSlots = this.products.filter(p => p.slots.length > 0);
    const hasSelectedSlots = productsWithSlots.filter(p => this.selectedSlots[p.id] !== null).length === productsWithSlots.length;
    return this.personalizationValid
      && (checkedClientConsents || checkedFallbacks)
      && hasSelectedSlots
      && this.isPublicTransportValid
      && this.isZipValid;
  }

  get acceptedClientsAGB() {
    const clientsWithAGB = Object.keys(this.agb).length;
    return clientsWithAGB > 0 ?
      this.personalizationDTO.termsAndConditions.length === clientsWithAGB :
      this.personalizationDTO.fallbackAgbChecked;
  }

  get acceptedClientsPrivacyStatements() {
    const clientsWithPrivacyStatements = Object.keys(this.privacyStatements).length;
    return clientsWithPrivacyStatements > 0 ?
      this.personalizationDTO.privacyStatements.length === clientsWithPrivacyStatements :
      this.personalizationDTO.fallbackPrivacyChecked;
  }

  eventsWithConsent(clientId: number) {
    return this.events.filter(e => clientId === e.clientId);
  }

  eventConsentString(clientId: number): string {
    const titles = this.eventsWithConsent(clientId).map(e => e.descriptionExternal);
    if (titles.length) {
      return titles.join(', ');
    }
    return '';
  }

  get clientsWithConsent() {
    const ids = Object.keys(this.privacyStatements);
    return this.clients.filter(c => ids.indexOf(`${c.id}`) >= 0);
  }

  get clientsWithConsentExist() {
    return this.clientsWithConsent.length > 0;
  }

  get shouldGenerateDownloadLinks() {
    return !!this.events.find((e) => e.provideDownloadLink);
  }

  async logout() {
    let success = true;
    if (!this.isGuestOrder && this.sessionDTO && this.sessionDTO.session) {
      success = await this.authService.logout(this.sessionDTO.session);
    }

    if (success) {
      this.$store.commit(CLEAR_IS_GUEST_ORDER);
      this.$store.commit(CLEAR_USER);
      this.$store.commit(CLEAR_BUYER_DATA);
      this.$store.commit(CLEAR_SESSION);
    }
  }
}
